import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ScoreRing } from "@/components/ScoreBadge";
import { useLang } from "@/lib/i18n";
import { ShieldCheck, ArrowRight, FileCheck2, AlertTriangle, Sparkles, Globe2, Share2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_LABELS = {
  fr: {
    generic_green: "Écologique / vert",
    climate_neutral: "Neutralité climatique",
    recycled: "Recyclé / recyclable",
    biodegradable: "Biodégradable / compostable",
    natural: "Naturel",
    bio: "Bio",
    eco_responsible: "Éco-responsable",
    carbon_offset: "Compensation carbone",
    sustainable: "Durable",
  },
  en: {
    generic_green: "Generic green",
    climate_neutral: "Climate neutral",
    recycled: "Recycled",
    biodegradable: "Biodegradable",
    natural: "Natural",
    bio: "Organic",
    eco_responsible: "Eco-friendly",
    carbon_offset: "Carbon offset",
    sustainable: "Sustainable",
  },
};

function categoryLabel(cat, lang) {
  return (CATEGORY_LABELS[lang] || CATEGORY_LABELS.en)[cat] || cat.replace(/_/g, " ");
}

export default function TrustPage() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/trust/${id}`).then(r => setD(r.data)).catch(() => setErr(true));
  }, [id]);

  const shareUrl = typeof window !== "undefined"
    ? `${window.location.origin}/api/share/trust/${id}`
    : "";

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: `EmpCo — Score conformité ${d.score}/100`, url: shareUrl });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success(t("trust.copied"));
      }
    } catch (e) { /* User cancelled share or clipboard denied — non-blocking */
      if (e?.name !== "AbortError") console.debug("Share aborted:", e?.message);
    }
  };

  if (err) return (
    <div className="App min-h-screen brand-mesh">
      <Header variant="public" />
      <main className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="trust-error">
        <p className="text-muted-foreground">{t("badge.notfound")}</p>
      </main>
      <Footer />
    </div>
  );
  if (!d) return (
    <div className="App min-h-screen brand-mesh"><Header variant="public" /><main className="max-w-3xl mx-auto px-6 py-24 text-center text-muted-foreground">…</main><Footer /></div>
  );

  const gradeColor = { A: "#4CAF6A", B: "#7CB342", C: "#F59E0B", D: "#F97316", E: "#EF4444" }[d.grade] || "#1F5A99";

  return (
    <div className="App min-h-screen brand-mesh">
      <Header variant="public" />
      <main className="max-w-4xl mx-auto px-6 py-16" data-testid="trust-page">
        <div className="hairline p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <img src="/brand/logo-icon.png" alt="EmpCo" className="h-10" />
            <div>
              <p className="eyebrow">{t("trust.eyebrow")}</p>
              <h1 className="text-2xl md:text-3xl mt-1" data-testid="trust-company">
                {d.company || d.url.replace(/^https?:\/\//, "").replace(/\/$/, "")}
              </h1>
              <p className="text-xs text-muted-foreground mt-1 break-all">
                <Globe2 className="w-3 h-3 inline mr-1" /> {d.url}
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-[220px_1fr] gap-8 items-center">
            <div className="flex flex-col items-center">
              <ScoreRing score={d.score} size={200} stroke={16} />
              <div className="mt-3 inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full font-medium" style={{ background: `${gradeColor}22`, color: gradeColor }}>
                <ShieldCheck className="w-3.5 h-3.5" /> {t("trust.grade")} {d.grade}
              </div>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-md border p-3 text-center">
                  <div className="text-2xl font-serif" style={{ color: "#EF4444" }} data-testid="trust-red">{d.red_count}</div>
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground mt-1">{t("trust.red")}</div>
                </div>
                <div className="rounded-md border p-3 text-center">
                  <div className="text-2xl font-serif" style={{ color: "#F59E0B" }} data-testid="trust-orange">{d.orange_count}</div>
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground mt-1">{t("trust.orange")}</div>
                </div>
                <div className="rounded-md border p-3 text-center">
                  <div className="text-2xl font-serif" style={{ color: "#1F5A99" }} data-testid="trust-proofs">{d.proofs_count}</div>
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground mt-1">{t("trust.proofs")}</div>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                {t("badge.page.scanned")} {new Date(d.scanned_at).toLocaleDateString()} · {d.pages_scanned} {t("report.pages")}
              </p>
              <div className={`inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full ${d.verified ? "tag-green" : "tag-orange"}`} data-testid="trust-verified">
                <ShieldCheck className="w-3.5 h-3.5" /> {d.verified ? t("badge.page.verified") : t("badge.page.free")}
              </div>
              {d.vault_verified && (
                <div className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full tag-green ml-2" data-testid="trust-vault-verified">
                  <ShieldCheck className="w-3.5 h-3.5" /> {t("vault.verified.badge")}
                </div>
              )}
            </div>
          </div>

          {d.categories && d.categories.length > 0 && (
            <div className="mt-10">
              <h2 className="text-lg font-serif mb-4 flex items-center gap-2"><Sparkles className="w-4 h-4 text-brand-blue" /> {t("trust.categories")}</h2>
              <div className="grid md:grid-cols-2 gap-3">
                {d.categories.slice(0, 8).map(c => (
                  <div key={c.category} className="flex items-center justify-between border rounded-md p-3" data-testid={`trust-cat-${c.category}`}>
                    <div className="text-sm">{categoryLabel(c.category, lang)}</div>
                    <div className="flex items-center gap-2 text-xs">
                      {c.red > 0 && <span className="tag-red px-2 py-0.5 rounded-full">{c.red}</span>}
                      {c.orange > 0 && <span className="tag-orange px-2 py-0.5 rounded-full">{c.orange}</span>}
                      {c.covered > 0 && <span className="tag-green px-2 py-0.5 rounded-full inline-flex items-center gap-1"><FileCheck2 className="w-3 h-3" />{c.covered}</span>}
                      {c.non_compliant > 0 && <span className="tag-red px-2 py-0.5 rounded-full inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{c.non_compliant}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className="text-xs text-muted-foreground mt-10 leading-relaxed">{t("badge.page.disclaimer")}</p>

          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={share} className="btn-secondary inline-flex" data-testid="trust-share">
              <Share2 className="w-4 h-4" /> {t("trust.share")}
            </button>
            <Link to="/" className="btn-primary inline-flex" data-testid="trust-cta">
              {t("trust.cta")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
